/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { Product } from '@api/catalog/products/products.models';
import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { Ga4ItemListVisible } from '@shared/analytics/GA4/store/ga4-analytics.actions';
import { ZenitListItemVisible } from '@shared/analytics/ZENIT/store/actions/zenit.actions';
import { ProductViewPlace } from '@shared/product/related-products/domain/models/related-products.models';

export interface ViewItemServiceConfig {
    itemsBatch: number; // Tamaño máximo del buffer antes de que envie el evento
    delayMilisecondsUntilEvent: number; // Delay en milisegundos del tiempo que esperará antes de enviar el evento automaticamente (Para su uso fuera de la clase)
    firstTimeViewTreshold: number[]; // Umbral a partir del cual se considera que un item es visible la primera vez que se ven items (isFirstTime == true)
    standardViewTreshold: number[]; // Umbral a partir del cual se considera que un item es visible después de la primera vez que se ven items (isFirstTime == false)
    isOnce: boolean; // Si es true envía lo que hay en el buffer (aunque no cumpla otras condiciones), lo vacía y luego deja de observar el elemento
    rootMargin?: string; // Márgenes respecto al elemento padre
}

@Injectable()
export class ViewItemService {
    /** Propiedades de acceso desde los effects de control */
    public readonly config: ViewItemServiceConfig = {
        itemsBatch: 15,
        delayMilisecondsUntilEvent: 60000, // 60 segundos
        firstTimeViewTreshold: [0.3, 1], // El elemento se ve por lo menos un 30%
        standardViewTreshold: [0.50, 1], // El elemento se ve por lo menos un 50%
        isOnce: false,
    };

    private isFirstTime: boolean = true;

    private productsViewed: Set<Product> = new Set();

    private productsBuffer: Product[] = [];

    private observer: IntersectionObserver;

    constructor(
        private readonly store: Store,
    ) {
    }

    public getIsFirstTime(): boolean {
        return this.isFirstTime;
    }

    public resetIsFirstTime(): void {
        this.isFirstTime = true;
        this.productsViewed = new Set();
    }

    /* Listas cuyos items se van cargando de manera asíncrona (Ej. parrillas) */
    @BrowserOnly<ViewItemService, undefined>(undefined)
    public actionWhenVisibleAsync(
        product: Product,
        element: Element,
        place: ProductViewPlace,
        eventFrom: EventFrom,
        relatedsSectionId: string,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        const state = {
            isFirstTime: true,
            productsViewedGA4: [],
            productsViewedZenit: [],
            timers: [],
        };
        this.observer = new IntersectionObserver((entries, observer) => {
            this.actionWhenVisibleAsyncCallback(
                state,
                { entries, observer, element },
                product,
                place,
                eventFrom,
                false,
                relatedsSectionId,
                overrideConfig
            );
        }, { threshold: [...this.getThreshold(overrideConfig)], rootMargin: overrideConfig?.rootMargin });
        this.observer.observe(element);
    }

    public viewProduct(
        product: Product,
        isPassthrough: boolean,
        isReset?: boolean,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): Product[] {
        const returnProducts = this.getReturnProducts(product, isPassthrough, isReset, overrideConfig);

        const isProductViewed = this.productsViewed.has(product);
        if (product && !isProductViewed) {
            this.productsBuffer.push(product);
            this.productsViewed.add(product);
        }

        return returnProducts;
    }

    public unobserve(element: Element): void {
        this.observer.unobserve(element);
    }

    private asyncCallbackTimeout(
        state: { isFirstTime: boolean; productsViewedGA4: Product[]; productsViewedZenit: Product[]; timers: number[] },
        entries: IntersectionObserverEntry[],
        product: Product,
        eventFrom: EventFrom,
        timeoutTime: number,
        place: ProductViewPlace,
        relatedsSectionId: string,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): number {
        return setTimeout(() => {
            this.actionWhenVisibleAsyncCallback(
                state,
                { entries, observer: null, element: null },
                product,
                place,
                eventFrom,
                true,
                relatedsSectionId,
                overrideConfig
            );
        }, timeoutTime) as unknown as number;
    }

    private getThreshold(overrideConfig?: Partial<ViewItemServiceConfig>): number[] {
        if (this.isFirstTime) return overrideConfig?.firstTimeViewTreshold || this.config.firstTimeViewTreshold;
        return overrideConfig?.standardViewTreshold || this.config.standardViewTreshold;
    }

    private handleZenitLogic(
        state: { isFirstTime: boolean; productsViewedGA4: Product[]; productsViewedZenit: Product[]; timers: number[] },
        entries: IntersectionObserverEntry[],
        product: Product,
        eventFrom: EventFrom,
        relatedsSectionId: string,
        place: ProductViewPlace,
        timeoutEnd: boolean,
    ): void {
        if (!product) {
            return;
        }
        // Si el producto es visible y no se ha visto previamente registra el producto para evitar duplicación e inicia el temporizador de 1s
        if (entries[0].isIntersecting && !timeoutEnd
            && !state.productsViewedZenit.find((prod) => prod.id === product.id)
        ) {
            state.productsViewedZenit.push(product);
            state.timers.push(this.asyncCallbackTimeout(state, entries, product, eventFrom, 1000, place, relatedsSectionId));
        }
        // Si el producto deja de ser visible y es un producto registrado limpia el temporizador y elimina los registros de este y el producto
        if (!entries[0].isIntersecting && !timeoutEnd
            && state.productsViewedZenit.find((prod) => prod.id === product.id)
        ) {
            const productIndex = state.productsViewedZenit.indexOf(product);
            clearTimeout(state.timers[productIndex]);
            state.timers.splice(productIndex, 1);
            state.productsViewedZenit.splice(productIndex, 1);
        }
        // Si ha finalizado el temporizador mientras el producto está visible lanza el evento de producto visto para Zenit
        if (timeoutEnd && entries[0].isIntersecting) {
            this.store.dispatch(ZenitListItemVisible({
                eventFrom,
                place,
                product,
                relatedsSectionId,
            }));
        }
    }

    private handleGA4Logic(
        state: { isFirstTime: boolean; productsViewedGA4: Product[]; productsViewedZenit: Product[]; timers: number[] },
        product: Product,
        place: ProductViewPlace,
        intersectionData: { entries: IntersectionObserverEntry[]; observer: IntersectionObserver; element: Element },
        eventFrom: EventFrom,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        const gridLocations = ['PRODUCT_GRID', 'PRODUCT_CUSTOM_CATEGORY'];
        if (state.isFirstTime) state.productsViewedGA4 = [];
        /** Reutilizar primera condicion si se quiere utilizar con listas de productos, si se requirieren condiciones diferentes para otros casos crear nuevo IF */
        // Si se ha proporcionado un producto que está en un grid, es visible y no se ha visto previamente registra el producto y envia el evento de producto visto para GA4
        if (product?.id && gridLocations.includes(place) && intersectionData.entries[0].isIntersecting
            && !state.productsViewedGA4.find((prod) => prod.id === product.id)) {
            state.productsViewedGA4.push(product);
            state.isFirstTime = false;
            this.store.dispatch(Ga4ItemListVisible({
                eventFrom,
                product: { ...product, place: place } as Product,
            }));
            // Enviar lo que hay en el buffer cuando se especifica en la overrideConfig o cuando se llega al final de un elemento espeficicado
            // (Ej. Enviar el resto de productos pendientes de enviar al llegar al final de una parrilla)
        } else if (overrideConfig?.isOnce && intersectionData.entries[0].boundingClientRect.bottom < window.innerHeight) {
            this.store.dispatch(Ga4ItemListVisible({
                eventFrom,
                product: undefined,
                isPassthrough: true,
            }));
            intersectionData.observer.unobserve(intersectionData.element);
        }
    }

    private actionWhenVisibleAsyncCallback(
        state: { isFirstTime: boolean; productsViewedGA4: Product[]; productsViewedZenit: Product[]; timers: number[] }, // Estado interno de la función de callback con sus valores iniciales
        intersectionData: { entries: IntersectionObserverEntry[]; observer: IntersectionObserver; element: Element },
        product: Product,
        place: ProductViewPlace,
        eventFrom: EventFrom,
        timeoutEnd: boolean,
        relatedsSectionId: string,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): void {
        this.handleZenitLogic(state, intersectionData.entries, product, eventFrom, relatedsSectionId, place, timeoutEnd);
        this.handleGA4Logic(state, product, place, intersectionData, eventFrom, overrideConfig);
    }

    private getCountProductsActualInBuffer() {
        return this.productsBuffer.reduce((acc, value) => {
            if (value.gridElemType === 'BUNDLE') {
                return value.bundleProducts.length + acc;
            }
            return acc + 1;
        }, 0);
    }

    private getCountWithNewProduct(product: Product) {
        let countWithNewProduct = this.getCountProductsActualInBuffer() ?? 0;

        if (product?.gridElemType === 'BUNDLE') {
            countWithNewProduct += product.bundleProducts.length;
        } else {
            countWithNewProduct += 1;
        }

        return countWithNewProduct;
    }

    private getReturnProducts(
        product: Product,
        isPassthrough: boolean,
        isReset?: boolean,
        overrideConfig?: Partial<ViewItemServiceConfig>
    ): Product[] {
        let returnProducts: Product[] = [];

        const countWithNewProduct = this.getCountWithNewProduct(product);
        if (countWithNewProduct > (overrideConfig?.itemsBatch || this.config.itemsBatch) || isPassthrough) {
            this.isFirstTime = false;
            returnProducts = this.productsBuffer;
            this.productsBuffer = [];
            if (isReset) {
                this.resetIsFirstTime();
            }
        }

        return returnProducts;
    }

    /* Estas funciones son para la observación de elementos cuyos items se cargan de forma síncrona (de una sola vez) lo que previamente era necesario para los módulos de relacionados.
        Quedan comentadas para su uso en caso de que vuelva a ser necesario */

    /* Listas cuyos items se cargan de una vez */
    // public actionWhenVisibleSync(
    //     products: Product[],
    //     element: Element,
    //     place: string,
    //     eventFrom: EventFrom,
    //     baseProduct: Product,
    // eslint-disable-next-line no-secrets/no-secrets
    //     overrideConfig?: Partial<ViewItemServiceConfig>,
    // ) {
    //     const observer = new IntersectionObserver((entries, obs) => {
    // eslint-disable-next-line no-secrets/no-secrets
    //         this.actionWhenVisibleSyncCallback(products, entries, obs, element, eventFrom, place, baseProduct);
    //     }, { threshold: [...this.getThreshold(overrideConfig)] });
    //     observer.observe(element);
    // }

    // private actionWhenVisibleSyncCallback(
    //     products: Product[],
    //     entries: IntersectionObserverEntry[],
    //     obs: IntersectionObserver,
    //     element: Element,
    //     eventFrom: EventFrom,
    //     place: string,
    //     baseProduct: Product,
    // ) {
    //     if (!!products?.length && entries[0].isIntersecting) {
    //         const _products: any[] = [...products];
    //         _products[0] = { ...products[0], place };
    //
    //         this.store.dispatch(Ga4Event({
    //             name: GA4EventType.view_item_list,
    //             eventFrom,
    //             data: {
    //                 items: [..._products],
    //                 baseProduct,
    //             },
    //         }));
    //         obs.unobserve(element);
    //     }
    // }
}
